import { jsx as _jsx } from "react/jsx-runtime";
import { DatePicker as MuiDatePicker, } from '@mui/x-date-pickers/DatePicker';
import { addYears, subYears } from 'date-fns';
import { forwardRef, useCallback, useMemo, useState, } from 'react';
import { isDateInRange } from '@zurich/common/forms';
import { useTranslation } from '@zurich/common/hooks';
import { useTheme } from '../../hooks';
import { Icon } from '../Icon';
import { TextField } from '../TextField';
const maskMap = {
    en: '__/__/____',
    de: '__.__.____',
};
export const DatePicker = forwardRef(function DatePicker(props, ref) {
    const theme = useTheme();
    const { label, OpenPickerButtonProps, className, onChange, TextFieldProps, value, views, mask, error, helperText, required, dataName, desktopModeMediaQuery = theme.breakpoints.up('sm'), // We override the default to make our Cypress tests work
    disableComponentErrorHandling, ...otherProps } = props;
    const { t, i18n: { language }, } = useTranslation();
    const [invalidInput, setInvalidInput] = useState(false);
    const openPickerButtonProps = useMemo(() => ({
        'aria-label': 'change date',
        ...(OpenPickerButtonProps || {}),
    }), [OpenPickerButtonProps]);
    const handleChange = useCallback((date, value) => {
        if (
        // either a valid date or empty, they should be both accepted
        (date &&
            isDateInRange(date, {
                from: subYears(new Date(), 200),
                to: addYears(new Date(), 200),
            })) ||
            !date) {
            // it is necessary to set time to start of the day because of timezone issues in picker: https://github.com/mui/material-ui-pickers/issues/1526
            const startOfDate = date ? new Date(date.setHours(0, 0, 0, 0)) : null;
            onChange(startOfDate, value);
            setInvalidInput(false);
        }
        else {
            setInvalidInput(true);
        }
    }, [onChange]);
    const hasValue = !!value;
    const TextFieldComponent = useCallback((params) => {
        const allProps = {
            ...(TextFieldProps || {}),
            ...params,
            required,
            dataName,
            helperText: helperText ||
                (!disableComponentErrorHandling &&
                    params.error &&
                    t('common:validation.invalid_input_2')) ||
                (!disableComponentErrorHandling &&
                    invalidInput &&
                    t('common:validation.invalid_input_2')) ||
                params.helperText,
            error: (!disableComponentErrorHandling && (params.error || invalidInput)) ||
                error,
        };
        return _jsx(TextField, { hasValue: hasValue, ...allProps });
    }, [
        disableComponentErrorHandling,
        TextFieldProps,
        required,
        dataName,
        helperText,
        t,
        invalidInput,
        error,
        hasValue,
    ]);
    return (_jsx(MuiDatePicker, { desktopModeMediaQuery: desktopModeMediaQuery, views: views ?? ['year', 'month', 'day'], mask: mask || maskMap[language], className: className, inputRef: ref, renderInput: TextFieldComponent, label: label || t('common:date'), components: {
            OpenPickerIcon: KeyboardIcon,
        }, OpenPickerButtonProps: {
            edge: false,
            ...openPickerButtonProps,
        }, onChange: handleChange, value: value, ...(otherProps.disableFuture
            ? {
                maxDate: new Date(),
            }
            : {}), ...otherProps }));
});
const KeyboardIcon = () => _jsx(Icon, { variant: "calendar-2", color: "secondary" });
